/**
 * Hook that evaluates surveyForm visibility rules against current form values
 * and returns the set of field IDs that should be visible.
 *
 * Supports:
 * - SimpleEqualityQuestionVisibility: field value === expected value
 * - LogicalQuestionVisibility (Or): any child visibility satisfied
 * - Chained visibility: if a parent is hidden, dependents are also hidden
 * - Dropdown (Option): single selected value compared directly
 * - Checkboxes (MultipleOptions): comma-separated values, any match counts
 */

import * as React from 'react';
import {
	FilterOperator,
	LogicalQuestionVisibility,
	QuestionVisibility,
	SimpleEqualityQuestionVisibility,
	SurveyForm,
	SurveyPart,
} from '../../../../../extViewmodels';
import { IPublicFileAttachment } from '../../../../models/Api';
import { isSurveySection, type SurveySection } from '../types';

type FormValues = Record<string, string | IPublicFileAttachment[]>;

/**
 * Checks whether a single visibility condition is satisfied given current form values.
 */
const isVisibilitySatisfied = (
	visibility: QuestionVisibility,
	formValues: FormValues,
	visibleFieldIds: Set<string>,
	depth: number
): boolean => {
	// Guard against depth recursion
	if (depth > 20) return false;

	if (visibility._type === 'SimpleEqualityQuestionVisibility') {
		const simple = visibility as SimpleEqualityQuestionVisibility;

		// If the field we depend on is itself not visible, condition is not met
		if (!visibleFieldIds.has(simple.fieldId)) return false;

		const currentValue = formValues[simple.fieldId];
		if (typeof currentValue !== 'string') return false;

		// For checkboxes (MultipleOptions), value is comma-separated IDs
		const selectedValues = currentValue.split(',').filter(v => v.trim() !== '');
		return selectedValues.includes(simple.fieldValue);
	}

	if (visibility._type === 'LogicalQuestionVisibility') {
		const logical = visibility as LogicalQuestionVisibility;

		if (logical.logicalOperator === FilterOperator.Or) {
			return logical.visibilities.some(v => isVisibilitySatisfied(v, formValues, visibleFieldIds, depth + 1));
		}

		// And is kept for forward-compatibility; the V2 editor ships Or-only in the initial release.
		if (logical.logicalOperator === FilterOperator.And) {
			return logical.visibilities.every(v => isVisibilitySatisfied(v, formValues, visibleFieldIds, depth + 1));
		}
	}

	return false;
};

/**
 * Computes the set of visible field IDs based on surveyForm rules and current values.
 *
 * Fields without a visibleWhen rule are always visible.
 * Fields with visibleWhen are visible only when their condition is satisfied.
 * Evaluation respects ordering — a field can only depend on fields earlier in the list.
 *
 * V2 section support: parts with _type "SurveySection" are treated as containers.
 * A section-level visibleWhen gates all nested questions; if unsatisfied, every question
 * in that section is hidden. Within a visible section, each question's own visibleWhen
 * is evaluated normally. Flat (V1-style) parts continue to work unchanged.
 */
export const computeVisibleFields = (surveyForm: SurveyForm | undefined, formValues: FormValues): Set<string> => {
	const visibleFieldIds = new Set<string>();

	if (!surveyForm?.surveyParts?.length) {
		return visibleFieldIds; // Empty set — caller should treat as "show all"
	}

	const addQuestion = (question: { fieldId?: string; visibleWhen?: QuestionVisibility | null }) => {
		if (!question.fieldId) return;
		if (!question.visibleWhen) {
			// No condition — always visible
			visibleFieldIds.add(question.fieldId);
		} else if (isVisibilitySatisfied(question.visibleWhen, formValues, visibleFieldIds, 0)) {
			visibleFieldIds.add(question.fieldId);
		}
		// Otherwise: not visible (not added to set)
	};

	// ORDERING CONSTRAINT: parts are evaluated in document order. A section's
	// visibleWhen (and each question's visibleWhen) is resolved against only the
	// fieldIds that have already been registered in visibleFieldIds by earlier
	// surveyParts. A same-section or forward reference will always evaluate as
	// hidden because the referenced fieldId has not been added to visibleFieldIds
	// yet. This is intentional — it mirrors the document-order rule and prevents
	// non-deterministic evaluation of circular or forward-referencing conditions.
	for (const part of surveyForm.surveyParts) {
		if (isSurveySection(part as SurveyPart)) {
			const section = part as SurveySection;
			// Section-level gate evaluated BEFORE any of the section's own questions
			// are registered — so visibleWhen here may only reference fieldIds from
			// earlier surveyParts; same-section or forward references always evaluate
			// hidden (silently). The same ordering rule applies to question-level rules.
			if (section.visibleWhen && !isVisibilitySatisfied(section.visibleWhen, formValues, visibleFieldIds, 0)) {
				continue;
			}
			for (const question of section.questions ?? []) addQuestion(question);
		} else {
			addQuestion(part as { fieldId?: string; visibleWhen?: QuestionVisibility | null });
		}
	}

	return visibleFieldIds;
};

/**
 * React hook that returns the set of visible field IDs.
 *
 * @param surveyForm - The survey's form layout with visibility rules
 * @param customProperties - Current form values keyed by field ID
 * @returns Set of field IDs that should be visible. If surveyForm has no parts,
 *          returns undefined (meaning no filtering — show all fields).
 */
export const useConditionalVisibility = (
	surveyForm: SurveyForm | undefined,
	customProperties: FormValues
): Set<string> | undefined => {
	return React.useMemo(() => {
		if (!surveyForm?.surveyParts?.length) {
			return undefined; // No surveyForm — show all fields
		}
		return computeVisibleFields(surveyForm, customProperties);
	}, [surveyForm, customProperties]);
};
